<template>
  <sdui-login :show-arrow="false">
    <header-navigation not-fixed>
      <template #left-actions v-if="!isLoadingSchool">
        <div class="ml-4">
          <p class="mb-0 fz-base font-weight-bold text-break-hyphens">{{ school.name }}</p>
        </div>
      </template>

      <template #right-actions>
        <div class="text-right">
          <b-button
            variant="link"
            class="hitbox-min-size d-flex align-items-center"
            @click="$router.replace({ name: 'login-select-school' })"
          >
            <span class="sr-only">{{ $t('management.edit_school.school') }}</span>
            {{ $t('login.login_sign_in.change_school') }}
          </b-button>
        </div>
      </template>
    </header-navigation>

    <main>
      <h1 class="sr-only">{{ $t('global.app_name') }} {{ $t('login.login_sign_in.login') }}</h1>

      <b-container class="my-6">
        <login-welcome-to />

        <template v-if="!isLoadingSchool">
          <!-- default login container if no SSO is activated -->
          <login-sign-in-container v-if="!singleSignOnActivated && !groupIam.length" />

          <!-- login container with all SSO options + default login -->
          <div v-else>
            <login-register-by-activation-code v-if="brandingConfig.login.registration_code" />

            <template v-if="brandingConfig.login.registration_code && (showDefaultLoginForSSO || !!groupIam.length || hasTmsId)">
              <sdui-labeled-divider
                data-test="register-sso-divider"
                :divider-word="$t('login.login_sign_in.separator')"
              />
            </template>

            <!-- SSO login buttons -->

            <SLink
              v-for="{ description_name, uri, provider_icon } in groupIam"
              :key="description_name"
              :open-externally="false"
              appearance="b-link"
              class="d-flex sso btn btn-primary btn-lg align-items-center mb-1"
              :data-cy="`login-with-${formatIdentityProviderName(description_name)}`"
              :href="`${uri}&redirect_uri=${idmCallbackUrl}`"
              :disabled="isLoadingSchool"
              force-hide-external-link-icon
              @click="handleKeycloakAuth"
            >
              <img v-if="provider_icon"
                   :src="getIconImage(provider_icon)"
                   alt=""
                   class="btn-icon"
                   loading="lazy"
              />
              <span class="flex-grow-1 align-items-center d-flex justify-content-center">
                {{ $t('login.login_sign_in.sso_login_with', { servicename: description_name }) }}
                <sdui-icon class="icon-externallink-o ml-1" />
              </span>
              <div class="btn-icon" />
            </SLink>

            <SLink
              v-for="{ description_name, uri, provider_icon } in externalIdentityProviders"
              :key="description_name"
              :open-externally="false"
              appearance="b-link"
              class="d-flex sso btn btn-primary btn-lg align-items-center mb-1"
              :data-cy="`login-with-${formatIdentityProviderName(description_name)}`"
              :href="`${uri}&redirect_uri=${idmCallbackUrl}`"
              :disabled="isLoadingSchool"
              force-hide-external-link-icon
              @click="(e) => handleSSOClick(e, `${uri}&redirect_uri=${idmCallbackUrl}`)"
            >
              <img v-if="provider_icon"
                   :src="getIconImage(provider_icon)"
                   alt=""
                   class="btn-icon"
                   loading="lazy"
              />
              <span class="flex-grow-1 align-items-center d-flex justify-content-center">
                {{ $t('login.login_sign_in.sso_login_with', { servicename: description_name }) }}
                <sdui-icon class="icon-externallink-o ml-1" />
              </span>
              <div class="btn-icon" />
            </SLink>

            <SLink
              v-if="isAndroid"
              appearance="b-link"
              class="d-flex justify-content-center"
              :href="$t('login.login_sign_in.sso_help_link')"
            >
              {{ $t('login.login_sign_in.sso_help') }}
            </SLink>

            <div
              v-if="showStandaloneForgotPassword"
              class="text-center mt-3"
              data-test="forgot-password-sso"
            >
              <b-button
                variant="link"
                class="p-3 font-weight-bold"
                @click="$router.push({ name: 'login-forgot-data' })"
              >
                {{ $t('login.login_sign_in.forgot_access_data') }}
              </b-button>
            </div>

            <template v-if="showDefaultLoginForSSO || !!groupIam.length">
              <sdui-labeled-divider
                v-if="showSSOAccordionDivider"
                data-test="sso-accordion-divider"
                :divider-word="$t('login.login_sign_in.separator')"
              />
              <!-- DEFAULT login -->
              <div class="accordion mt-6" role="tablist">
                <!-- expand accordion -->
                <b-button
                  v-if="!sduiLoginExpanded"
                  v-b-toggle="'sdui-login'"
                  size="lg"
                  type="submit"
                  variant="outline-secondary"
                  class="pt-2 sso btn-sdui"
                  data-cy="expand-sdui-login"
                  block
                  @click="sduiLoginExpanded = !sduiLoginExpanded"
                >
                  <div class="d-flex align-items-center">
                    <img :src="getIconImage('sdui_small')"
                         alt=""
                         class="btn-icon"
                         loading="lazy"
                    />
                    <span class="flex-grow-1">{{ hasExternalNonIamIdp ? $t('login.login_sign_in.submit_with_sdui') : $t('login.login_sign_in.passport_login_help') }}</span>
                    <div class="btn-icon" />
                  </div>
                </b-button>
                <b-collapse id="sdui-login"
                            v-model="sduiLoginExpanded"
                            role="tabpanel"
                >
                  <!-- login container -->
                  <login-sign-in-container
                    :single-sign-on-activated="singleSignOnActivated"
                    hide-registration
                  />
                </b-collapse>
              </div>
            </template>
          </div>
        </template>
        <sdui-loader v-else class="py-6" />
      </b-container>
    </main>
  </sdui-login>
</template>
<script>
import { useHead } from '@/composables/utils/useHead.ts'
import { useSduiI18n } from '@/composables/useSduiI18n.ts'
import { useStore } from '@/composables/useStore.ts'
import { watch } from 'vue'
import SduiLogin from 'src/layout/SduiLogin.vue'
import HeaderNavigation from 'src/components/miscellaneous/ui/common/HeaderNavigation.vue'
import brandingMixin from 'src/mixins/branding/brandingMixin.js'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import fileMixin from 'src/mixins/channels/fileMixin.js'
import LoginSignInContainer from 'src/components/login/LoginSignInContainer.vue'
import LoginRegisterByActivationCode from 'src/components/login/LoginRegisterByActivationCode.vue'
import SduiLabeledDivider from 'src/components/miscellaneous/ui/SduiLabeledDivider.vue'
import LoginWelcomeTo from 'src/components/login/LoginWelcomeTo.vue'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import Core from 'src/api/core.js'
import sentryConfig from 'src/store/sentry.js'
import { KeycloakAuth } from 'src/config/auth/KeycloakAuth.ts'
import { AUTH_TYPE } from 'src/config/constants.ts'
import native from '@/config/native.js'

export default {
  // TODO VUE3: This is a mix of Options API and Composition API which should be removed by refactoring
  // the whole component to Composition API. Right now it's needed because of useHead.
  setup() {
    const { t } = useSduiI18n()
    const { store } = useStore()
    const { setHead } = useHead()

    setHead({
      title: t('login.login_sign_in.page_title', { school: store.state.school?.name }),
    })

    watch(
      () => store.state.school,
      (newValue) => {
        if (!newValue) return
        setHead({ title: t('login.login_sign_in.page_title', { school: newValue.name }) })
      },
    )
  },

  name: 'LoginSignIn',

  components: {
    HeaderNavigation,
    SduiLogin,
    LoginSignInContainer,
    SduiLabeledDivider,
    LoginWelcomeTo,
    SduiLoader,
    LoginRegisterByActivationCode,
  },

  mixins: [brandingMixin, fileMixin, isMobileDeviceMixin],
  data: () => ({
    sduiLoginExpanded: false,
    windowHandle: null,
    isFetchingSchoolForNewUser: false,
  }),

  computed: {
    isAndroid () {
      return this.$store.state.isAndroid
    },

    isIOS () {
      return this.$store.state.isIOS
    },

    forceDefaultAuthentication() {
      return !!this.$route.query.showDefaultAuth
    },

    showDefaultLoginForSSO() {
      if (this.hasTmsId) return
      return !this.brandingConfig.login?.hideSduiLoginDefault || this.forceDefaultAuthentication
    },

    showStandaloneForgotPassword() {
      const hasSSO = this.singleSignOnActivated || !!this.groupIam.length
      if (!hasSSO) return false

      const accordionRenders = this.showDefaultLoginForSSO || !!this.groupIam.length
      if (!accordionRenders) return true

      return !this.sduiLoginExpanded
    },

    showSSOAccordionDivider() {
      const accordionRenders = this.showDefaultLoginForSSO || !!this.groupIam.length
      if (!accordionRenders) return false
      if (!this.singleSignOnActivated) return false
      return !this.brandingConfig.login?.registration_code
    },

    school() {
      return this.$store.state.school
    },

    isLoadingSchool() {
      return this.isFetchingSchoolForNewUser || !this.school || this.school?.slink !== this.$store.state.slink
    },

    externalIdentityProviders() {
      return this.school?.meta?.external_identity_providers || []
    },

    groupIam() {
      return this.school?.meta?.group_iam || []
    },

    singleSignOnActivated() {
      return !!this.externalIdentityProviders.length
    },

    SSOInSeparateWindow() {
      return window.self !== window.top
    },

    hasTmsId () {
      return this.school?.meta?.properties?.['iam.as_auth_driver.enabled'] && this.externalIdentityProviders.length
    },

    // "Log in with Sdui" for external IdP schools — "Having trouble?" is only for internal IAM schools.
    hasExternalNonIamIdp () {
      return this.singleSignOnActivated && !this.hasTmsId
    },

    idmCallbackUrl() {
      const path = this.$router.resolve({ name: 'sso-verify' }).href
      // Workaround: Also apply branding as query param until iOS reload issue was fixed (branding gets reset)
      const state = encodeURIComponent(
        JSON.stringify({
          branding: this.branding,
          slink: this.school.slink,
          goBackToParentWindow: this.SSOInSeparateWindow,
        }),
      )
      if (import.meta.env.VITE_LIVE_UPDATE_ENABLED === 'true') return `${import.meta.env.VITE_APP_URL}${path}&state=${state}`
      return `${window.location.origin}${path}&state=${state}`
    },
  },

  methods: {
    renamePage() {
      if (!this.school?.name) return
      const title = this.$t('login.login_sign_in.page_description', { school: this.school.name })
      document.querySelector('head meta[name="description"]').setAttribute('content', title)
    },

    resetPageTitle() {
      const content = this.$t('login.login_sign_in.page_default_description')
      document.querySelector('head meta[name="description"]').setAttribute('content', content)
    },

    setSchool: waitFor('fetch-school', async function () {
      const isAddingUser = this.$store.state.multiUsers.useNewLogin

      if (isAddingUser) this.isFetchingSchoolForNewUser = true

      try {
        // Force refetch when adding a user — cached school data (from /self or localStorage) may lack group_iam, hiding Keycloak SSO buttons
        if (!isAddingUser && this.school?.slink === this.$store.state.slink) return

        const { data } = await Core.getFindSchools({ params: { slink: this.$store.state.slink } })
        if (!data) return this.$router.replace({ name: 'login-select-school' })

        this.$store.commit('SET_SCHOOL', data)

        this.renamePage()
      } finally {
        this.isFetchingSchoolForNewUser = false
      }
    }),

    formatIdentityProviderName(identityProviderName) {
      if (!identityProviderName) return
      return identityProviderName.replace(/\s/g, '')
    },

    async handleKeycloakAuth(event) {
      event.preventDefault()

      const realm = this.school?.meta?.group_iam?.[0]?.realm_id
      if (!realm || !localStorage) {
        this.$toastNotify({
          type: 'error',
          text: this.$t('ui.connection_lost.title')
        })
        return
      }

      localStorage.setItem('kc-login-in-progress', 'true')
      localStorage.setItem('slink', this.school.slink)

      const kcAuth = await KeycloakAuth.getInstance(realm)

      if (!kcAuth) return

      // Force fresh login if adding another account from the same realm
      const existingUser = Object.values(this.$store.state.multiUsers.users || {})
        .find(u => u.realm === realm && u.authType === AUTH_TYPE.KEYCLOAK)

      // Prefer the browser session token — it matches the active Keycloak cookie.
      // Fall back to any user's stored idToken (may cause confirmation dialog if stale).
      const idTokenForLogout = KeycloakAuth.getBrowserSessionToken(realm) || existingUser?.idToken

      // Clear Keycloak session cookies via redirect to end_session_endpoint (web only)
      if (idTokenForLogout && !this.$store.state.isNative) {
        sessionStorage.setItem('kc-reauth-intent', JSON.stringify({ realm, slink: this.school.slink }))

        const baseUri = kcAuth.getPostLogoutRedirectUri().replace(/\/$/, '')
        const postLogoutUri = `${baseUri}/${this.school.slink}/login`
        window.location.href = kcAuth.buildSessionClearUrl(realm, idTokenForLogout, postLogoutUri)
        return
      }

      // Fallback: prompt for login (native apps, or when no idToken available)
      await kcAuth.signinRedirect(existingUser ? { prompt: 'login' } : undefined)
    },


    async handleSSOClick(event, url) {
      if (this.$store.state.isNative) {
        // iOS uses default href from SLink (no preventDefault called) instead of custom tab, as this works more reliable
        if (this.isIOS) return

        if (this.isAndroid) {
          // On android, don't follow the href but open it in a custom tab within the app (not opening the browser app).
          // This fixes issues with some default browsers not redirecting back to the app.
          event.preventDefault()
          await native.openUrlInAppBrowser(url)
          return
        }
      }

      // Apply this workaround only for iframes:
      // Issue: Websites loaded inside an iframe do not share storage (cookies, session, etc.) with the top-level site.
      // This breaks SSO as the IDP cannot access its session inside the iframe.
      // Additionally, Untis seems to force navigation from the iframe to the top-level window, causing the normal redirect flow to fail within an iframe.
      // Solution: If the current window is in an iframe, open a new window to handle the redirect at the top-level instead.
      if (!this.SSOInSeparateWindow) return

      event.preventDefault()
      this.windowHandle = window.open(url, 'OIDC', 'width=500,height=600')
    },

    async resumeKeycloakReauth() {
      const intentJson = sessionStorage.getItem('kc-reauth-intent')
      if (!intentJson) return

      sessionStorage.removeItem('kc-reauth-intent')

      try {
        const { realm } = JSON.parse(intentJson)
        if (!realm) return

        localStorage.setItem('kc-login-in-progress', 'true')

        const kcAuth = await KeycloakAuth.getInstance(realm)
        if (!kcAuth) return

        // Keycloak session cookies are now cleared — start fresh OIDC login
        await kcAuth.signinRedirect()
      } catch (e) {
        sentryConfig.addBreadcrumb({
          category: 'auth/resumeKeycloakReauth',
          message: `Failed to resume reauth: ${e}`,
          level: 'warning',
        })
      }
    },

    async verifySSOByNewWindow(event) {
      if (event.data?.name !== 'sso-code' || event.origin !== location.origin) return

      const slink = event.data?.slink
      const code = event.data?.code
      // As we in Firefox browser localStorage of pop-up window is a separate Storage and we don't have access from parent window we handle necessary data throw this event handler
      !slink && sentryConfig.throwError('error-when-handling-sso-login-pop-up-no-slink')
      !code && sentryConfig.throwError('error-when-handling-sso-login-pop-up-no-code')
      this.windowHandle?.close()

      this.$router.push({
        name: 'sso-verify',
        params: { slink },
        query: { code, state: JSON.stringify({ goBackToParentWindow: false, branding: this.branding }) },
      })
    },
  },

  async created() {
    if (!this.$store.state.slink) return this.$router.replace({ name: 'login-select-school' })
    this.sduiLoginExpanded = this.brandingConfig.login?.sduiLoginDefaultExpanded

    await this.setSchool()

    this.renamePage()

    // Resume Keycloak login after session cookie clearing redirect
    await this.resumeKeycloakReauth()
  },

  mounted() {
    window.addEventListener('message', this.verifySSOByNewWindow)
  },

  beforeUnmount() {
    this.resetPageTitle()
    window.removeEventListener('message', this.verifySSOByNewWindow)
  },
}
</script>

<style scoped lang="scss">
:deep(.timetable-bottom-message > .alert) {
  padding: 12px !important;
}
</style>
